/**
 * Authorization utilities for role and permission checking
 */

import type { UserRole } from '@/types'

/**
 * Permission definitions for the application
 */
export const PERMISSIONS = {
    // Product permissions
    VIEW_PRODUCTS: 'view_products',
    CREATE_PRODUCTS: 'create_products',
    EDIT_PRODUCTS: 'edit_products',
    DELETE_PRODUCTS: 'delete_products',
    IMPORT_PRODUCTS: 'import_products',
    EXPORT_PRODUCTS: 'export_products',

    // Marking code permissions
    VIEW_MARKING_CODES: 'view_marking_codes',
    CREATE_MARKING_CODES: 'create_marking_codes',
    EDIT_MARKING_CODES: 'edit_marking_codes',
    DELETE_MARKING_CODES: 'delete_marking_codes',
    ORDER_MARKING_CODES: 'order_marking_codes',
    SCAN_MARKING_CODES: 'scan_marking_codes',

    // Task permissions
    VIEW_TASKS: 'view_tasks',
    CREATE_TASKS: 'create_tasks',
    ASSIGN_TASKS: 'assign_tasks',
    COMPLETE_OWN_TASKS: 'complete_own_tasks',
    COMPLETE_ANY_TASKS: 'complete_any_tasks',

    // Document permissions
    VIEW_DOCUMENTS: 'view_documents',
    GENERATE_DOCUMENTS: 'generate_documents',
    DOWNLOAD_DOCUMENTS: 'download_documents',
    SUBMIT_DOCUMENTS: 'submit_documents',

    // User management permissions
    VIEW_USERS: 'view_users',
    CREATE_USERS: 'create_users',
    EDIT_USERS: 'edit_users',
    DELETE_USERS: 'delete_users',

    // System permissions
    VIEW_SYSTEM_LOGS: 'view_system_logs',
    MANAGE_INTEGRATIONS: 'manage_integrations',
    MANAGE_ORGANIZATION: 'manage_organization',
    VIEW_ORGANIZATION_SETTINGS: 'view_organization_settings'
} as const

/**
 * Role definitions with their default permissions
 */
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
    admin: [
        // All permissions
        ...Object.values(PERMISSIONS)
    ],

    manager: [
        // Product management
        PERMISSIONS.VIEW_PRODUCTS,
        PERMISSIONS.CREATE_PRODUCTS,
        PERMISSIONS.EDIT_PRODUCTS,
        PERMISSIONS.DELETE_PRODUCTS,
        PERMISSIONS.IMPORT_PRODUCTS,
        PERMISSIONS.EXPORT_PRODUCTS,

        // Marking code management
        PERMISSIONS.VIEW_MARKING_CODES,
        PERMISSIONS.CREATE_MARKING_CODES,
        PERMISSIONS.EDIT_MARKING_CODES,
        PERMISSIONS.ORDER_MARKING_CODES,
        PERMISSIONS.SCAN_MARKING_CODES,

        // Task management
        PERMISSIONS.VIEW_TASKS,
        PERMISSIONS.CREATE_TASKS,
        PERMISSIONS.ASSIGN_TASKS,
        PERMISSIONS.COMPLETE_OWN_TASKS,
        PERMISSIONS.COMPLETE_ANY_TASKS,

        // Document management
        PERMISSIONS.VIEW_DOCUMENTS,
        PERMISSIONS.GENERATE_DOCUMENTS,
        PERMISSIONS.DOWNLOAD_DOCUMENTS,
        PERMISSIONS.SUBMIT_DOCUMENTS,

        // User management
        PERMISSIONS.VIEW_USERS,
        PERMISSIONS.CREATE_USERS,
        PERMISSIONS.EDIT_USERS,

        // Limited system access
        PERMISSIONS.VIEW_ORGANIZATION_SETTINGS
    ],

    operator: [
        // Product viewing
        PERMISSIONS.VIEW_PRODUCTS,
        PERMISSIONS.EXPORT_PRODUCTS,

        // Marking code operations
        PERMISSIONS.VIEW_MARKING_CODES,
        PERMISSIONS.SCAN_MARKING_CODES,

        // Task operations
        PERMISSIONS.VIEW_TASKS,
        PERMISSIONS.COMPLETE_OWN_TASKS,

        // Document viewing
        PERMISSIONS.VIEW_DOCUMENTS,
        PERMISSIONS.DOWNLOAD_DOCUMENTS
    ],

    viewer: [
        // Read-only access
        PERMISSIONS.VIEW_PRODUCTS,
        PERMISSIONS.VIEW_MARKING_CODES,
        PERMISSIONS.VIEW_TASKS,
        PERMISSIONS.VIEW_DOCUMENTS
    ]
}

/**
 * Authorization utility class
 */
export class AuthorizationUtil {

    /**
     * Check if user has a specific permission
     */
    hasPermission(permission: string, authStore: any): boolean {
        return authStore.hasPermission(permission)
    }

    /**
     * Check if user has any of the specified permissions
     */
    hasAnyPermission(permissions: string[], authStore: any): boolean {
        return permissions.some(permission => this.hasPermission(permission, authStore))
    }

    /**
     * Check if user has all of the specified permissions
     */
    hasAllPermissions(permissions: string[], authStore: any): boolean {
        return permissions.every(permission => this.hasPermission(permission, authStore))
    }

    /**
     * Check if user has a specific role
     */
    hasRole(role: UserRole, authStore: any): boolean {
        return authStore.hasRole(role)
    }

    /**
     * Check if user has any of the specified roles
     */
    hasAnyRole(roles: UserRole[], authStore: any): boolean {
        return roles.some(role => this.hasRole(role, authStore))
    }

    /**
     * Check if user has all of the specified roles
     */
    hasAllRoles(roles: UserRole[], authStore: any): boolean {
        return roles.every(role => this.hasRole(role, authStore))
    }

    /**
     * Check if user is admin
     */
    isAdmin(authStore: any): boolean {
        return this.hasRole('admin', authStore)
    }

    /**
     * Check if user is manager or above
     */
    isManagerOrAbove(authStore: any): boolean {
        return this.hasAnyRole(['admin', 'manager'], authStore)
    }

    /**
     * Check if user is operator or above
     */
    isOperatorOrAbove(authStore: any): boolean {
        return this.hasAnyRole(['admin', 'manager', 'operator'], authStore)
    }

    /**
     * Check if user can access a specific organization
     */
    canAccessOrganization(organizationId: string, authStore: any): boolean {
        return authStore.accessibleOrganizations.some(org => org.id === organizationId)
    }

    /**
     * Check if user is in the current organization
     */
    isInCurrentOrganization(organizationId: string, authStore: any): boolean {
        return authStore.currentOrganization?.id === organizationId
    }

    /**
     * Get user's effective permissions (role-based + explicit)
     */
    getEffectivePermissions(authStore: any): string[] {
        const user = authStore.user
        if (!user) return []

        // Get role-based permissions
        const rolePermissions = ROLE_PERMISSIONS[user.role] || []

        // Get explicit permissions from user
        const userPermissions = user.permissions || []

        // Combine and deduplicate
        return [...new Set([...rolePermissions, ...userPermissions])]
    }

    /**
     * Check if user can perform a specific action on a resource
     */
    canPerformAction(action: string, authStore: any, resource?: any): boolean {
        // Basic permission check
        if (!this.hasPermission(action, authStore)) {
            return false
        }

        // Additional resource-specific checks can be added here
        // For example, checking if user owns the resource or is in the same organization
        if (resource) {
            // Example: Check if user can edit their own tasks vs any tasks
            if (action === PERMISSIONS.COMPLETE_ANY_TASKS) {
                return this.isManagerOrAbove(authStore)
            }

            if (action === PERMISSIONS.COMPLETE_OWN_TASKS && resource.assignedTo) {
                return resource.assignedTo === authStore.user?.id
            }
        }

        return true
    }

    /**
     * Get user's role hierarchy level (higher number = more permissions)
     */
    getRoleLevel(authStore: any): number {
        const user = authStore.user
        if (!user) return 0

        const roleLevels: Record<UserRole, number> = {
            viewer: 1,
            operator: 2,
            manager: 3,
            admin: 4
        }

        return roleLevels[user.role] || 0
    }

    /**
     * Check if user has higher or equal role level than specified
     */
    hasRoleLevel(minimumLevel: number, authStore: any): boolean {
        return this.getRoleLevel(authStore) >= minimumLevel
    }

    /**
     * Check if user can access a route based on its meta requirements
     */
    canAccessRoute(routeMeta: any, authStore: any): boolean {
        // Check authentication requirement
        if (routeMeta.requiresAuth && !authStore.isAuthenticated) {
            return false
        }

        // Check role requirement
        if (routeMeta.requiredRole && !this.hasRole(routeMeta.requiredRole, authStore)) {
            return false
        }

        // Check permission requirements
        if (routeMeta.requiredPermissions && routeMeta.requiredPermissions.length > 0) {
            if (!this.hasAllPermissions(routeMeta.requiredPermissions, authStore)) {
                return false
            }
        }

        // Check organization access
        if (routeMeta.allowedOrganizations && routeMeta.allowedOrganizations.length > 0) {
            const currentOrgId = authStore.currentOrganization?.id
            if (!currentOrgId || !routeMeta.allowedOrganizations.includes(currentOrgId)) {
                return false
            }
        }

        return true
    }
}

// Create singleton instance
export const authUtil = new AuthorizationUtil()

// Export convenience functions
export const hasPermission = (permission: string, authStore: any) => authUtil.hasPermission(permission, authStore)
export const hasAnyPermission = (permissions: string[], authStore: any) => authUtil.hasAnyPermission(permissions, authStore)
export const hasAllPermissions = (permissions: string[], authStore: any) => authUtil.hasAllPermissions(permissions, authStore)
export const hasRole = (role: UserRole, authStore: any) => authUtil.hasRole(role, authStore)
export const hasAnyRole = (roles: UserRole[], authStore: any) => authUtil.hasAnyRole(roles, authStore)
export const isAdmin = (authStore: any) => authUtil.isAdmin(authStore)
export const isManagerOrAbove = (authStore: any) => authUtil.isManagerOrAbove(authStore)
export const isOperatorOrAbove = (authStore: any) => authUtil.isOperatorOrAbove(authStore)
export const canPerformAction = (action: string, authStore: any, resource?: any) => authUtil.canPerformAction(action, authStore, resource)
export const canAccessRoute = (routeMeta: any, authStore: any) => authUtil.canAccessRoute(routeMeta, authStore)